import { useEffect, useState } from 'react';
import { api, errorMessage } from './api.ts';
import type { BuildJobRow, BuildsPayload } from '../../shared/api.ts';

type BuildsSnapshot = {
  builds: BuildJobRow[] | null;
  running: number;
  maxConcurrent: number;
  error: string | null;
};

/**
 * One shared poll of `GET /api/wizard/builds` for every surface that shows
 * in-flight builds: the header indicator, the dashboard's in-progress cards and
 * the ⚙ drawer's Builds panel.
 *
 * A module-level singleton rather than a per-component interval, because these
 * surfaces are mounted together — the drawer already polled the same endpoint
 * twice on its own (a 5s head count beside its panel's 3s list), and a header
 * indicator visible on every route would have made a third. Subscribers share
 * one timer and one in-flight request; the timer exists only while something is
 * mounted.
 */

const POLL_MS = 3000;

let state: BuildsSnapshot = { builds: null, running: 0, maxConcurrent: 0, error: null };
const subs = new Set<(s: BuildsSnapshot) => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let inFlight: Promise<void> | null = null;
let queued: Promise<void> | null = null;

function emit() {
  for (const fn of subs) fn(state);
}

/**
 * Re-read the list now.
 *
 * `{ fresh: true }` for a caller that has just CHANGED it — started an import,
 * killed a build. Without it, a call made a moment after the change can be
 * answered by a request that was already in the air before the change, which
 * reads on screen as the change not having happened: the library's new item
 * offered "Add the first slide" for a tick because the poll it triggered
 * predated the job it was asking about. At most one follow-up is queued, so a
 * burst of fresh callers still costs one extra request between them.
 */
export async function refreshBuilds(opts?: { fresh?: boolean }): Promise<void> {
  if (inFlight && opts?.fresh) {
    if (!queued) {
      queued = inFlight.then(() => {
        queued = null;
        return refreshBuilds();
      });
    }
    return queued;
  }
  // Collapse concurrent callers onto one request — three subscribers mounting
  // together must not fire three GETs.
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const d = await api.get<Partial<BuildsPayload>>('/api/wizard/builds');
      state = {
        builds: d.builds || [],
        running: typeof d.running === 'number' ? d.running : 0,
        maxConcurrent: typeof d.maxConcurrent === 'number' ? d.maxConcurrent : 0,
        error: null,
      };
    } catch (err) {
      // Keep the last good list. A failed poll used to wipe the drawer's whole
      // panel — list, slot count and Kill buttons — which is exactly what you
      // need when a build is stuck.
      state = { ...state, error: errorMessage(err) };
    }
    emit();
    inFlight = null;
  })();
  return inFlight;
}

function tick() {
  // A background tab needn't poll; nothing here is actionable unseen.
  if (!document.hidden) refreshBuilds();
}

/** Subscribe to the shared build list. Returns the latest snapshot:
 *  `{builds, running, maxConcurrent, error}` — `builds` is null until the first
 *  response lands. */
export function useBuilds(): BuildsSnapshot {
  const [snapshot, setSnapshot] = useState(state);
  useEffect(() => {
    subs.add(setSnapshot);
    if (!timer) timer = setInterval(tick, POLL_MS);
    // Late subscribers get whatever is already known immediately, then a fresh
    // read rather than waiting out the interval.
    setSnapshot(state);
    refreshBuilds();
    return () => {
      subs.delete(setSnapshot);
      if (subs.size === 0 && timer) {
        clearInterval(timer);
        timer = null;
      }
    };
  }, []);
  return snapshot;
}

/** Kill a build, then refresh so every subscriber sees it go at once. */
export async function killBuild(id: string): Promise<void> {
  await api.post(`/api/wizard/build-cancel/${id}`, {});
  await refreshBuilds({ fresh: true });
}

/** Statuses that still count as in-flight (a paused job is mid-build, waiting
 *  on a human's plan review). */
export const ACTIVE_BUILD_STATUSES = new Set<string>(['running', 'paused']);

export function isActiveBuild(b: BuildJobRow | null | undefined): boolean {
  return !!b && ACTIVE_BUILD_STATUSES.has(b.status);
}

/**
 * The in-flight build writing this deck, or null.
 *
 * Rides the shared poll above rather than adding an endpoint — the answer is
 * already in every `/api/wizard/builds` payload, keyed by slug, and the server
 * has the same predicate as `hasActiveJobForSlug`. Covers whole-deck engines
 * (`claude` / `fast` / `pi`) and section builds (`section`) alike, since a
 * section job carries the deck's slug too.
 *
 * `undefined` while the first poll is outstanding, so callers can tell "not
 * building" from "do not know yet" — gating a route on the latter would flash
 * the editor open for a tick before redirecting.
 */
export function useBuildForSlug(slug: string | null | undefined): BuildJobRow | null | undefined {
  const { builds } = useBuilds();
  if (!slug) return null;
  if (builds === null) return undefined;
  return builds.find((b) => b.slug === slug && isActiveBuild(b)) || null;
}

export function formatBuildAge(startedAt: number): string {
  const secs = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  return `${Math.round(mins / 60)}h ${mins % 60}m`;
}
